<script setup>
import { computed, ref } from 'vue';
import formatDate from '@/utils/format-date.ts';
import { areDatesEqual, isDateBetween } from '@/components/base-components/components/date-picker/utils/helpers.js';
import { VIEW, MODE, TYPE, HOURS_FORMAT } from '@/components/base-components/components/date-picker/index.js';

const props = defineProps({
    value:         {
        type: [Date, Array, Object],
    },
    mode:          {
        type:      String,
        default:   MODE.SINGLE,
        validator: value => Object.values(MODE).includes(value),
    },
    view:          {
        type:      String,
        default:   VIEW.MONTH,
        validator: value => Object.values(VIEW).includes(value),
    },
    type:          {
        type:      String,
        default:   TYPE.DEFAULT,
        validator: value => Object.values(TYPE).includes(value),
    },
    hoursFormat:   {
        type:      String,
        default:   HOURS_FORMAT.HOURS_24,
        validator: value => Object.values(HOURS_FORMAT).includes(value),
    },
    withTime:      {
        type: Boolean,
    },
    withSeconds:   {
        type: Boolean,
    },
    clearable:     {
        type: Boolean,
    },
    noHideOnClick: {
        type: Boolean,
    },
});

const emit = defineEmits({
    input:        null,
    'cell-click': null,
});

const model = computed({
    get: () => props.value,
    set: value => emit('input', value),
});

const hoveredDate = ref(undefined);

const range = computed(() => {
    if (props.mode !== MODE.RANGE) return { start: null, end: null };

    const { start, end } = model.value ?? {};

    if (start && end) return { start, end };

    const hover = hoveredDate.value;

    if (start && !end && hover) return hover < start ? { start: hover, end: start } : { start, end: hover };

    if (start) return { start, end: null };

    return { start: null, end: null };
});

const firstDate = computed(() => {
    if (props.mode === MODE.MULTIPLE) return model.value?.[0] ?? null;
    if (props.mode === MODE.RANGE) return model.value?.start ?? null;

    return model.value ?? null;
});

function isActiveCell(date) {
    if (!model.value) return false;
    return areDatesEqual(date, model.value, props.view, props.mode);
}

function clear() {
    model.value = {
        [MODE.SINGLE]:   null,
        [MODE.MULTIPLE]: [],
        [MODE.RANGE]:    { start: null, end: null },
    }[props.mode] ?? null;
}

function clickCell(value) {
    const mode = props.mode;
    const view = props.view;

    if (mode === MODE.SINGLE) {
        if (props.clearable && areDatesEqual(value, model.value, view)) {
            model.value = null;
        } else {
            if (model.value) {
                value.setHours(model.value.getHours());
                value.setMinutes(model.value.getMinutes());
                value.setSeconds(model.value.getSeconds());
            }
            model.value = value;
        }

        emit('cell-click');

        return;
    }

    if (mode === MODE.MULTIPLE) {
        const index = model.value.findIndex(date => areDatesEqual(value, date, view));
        if (index !== -1) {
            model.value = model.value.toSpliced(index, 1);
        } else {
            model.value = [...model.value, value].toSorted((a, b) => a - b);
        }
        return;
    }

    if (mode === MODE.RANGE) {
        const start = model.value?.start;
        const end = model.value?.end;

        if (props.clearable && start && end && (areDatesEqual(value, start, view) || areDatesEqual(value, end, view))) {
            model.value = { start: null, end: null };
            return;
        }

        // New range
        if (!start || (start && end)) {
            model.value = { start: value, end: null };
            return;
        }

        // Range continue
        if (value < start) {
            model.value = { start: value, end: start };
        } else {
            model.value = { start, end: value };
        }
    }
}

const cellActiveClass = computed(() => ({
    [TYPE.DEFAULT]:       'text-white bg-primary-600 hover:bg-primary-500',
    [TYPE.PRIMARY]:       'text-white bg-primary-600 hover:bg-primary-500',
    [TYPE.SECONDARY]:     'text-white bg-secondary-950 hover:bg-secondary-850',
    [TYPE.PRIMARY_OLD]:   'text-white bg-science-blue-500 hover:bg-science-blue-400',
    [TYPE.SECONDARY_OLD]: 'text-white bg-science-blue-800 hover:bg-science-blue-700',
    [TYPE.SUCCESS]:       'text-white bg-emerald-600 hover:bg-emerald-500',
    [TYPE.WARNING]:       'text-white bg-orange-500 hover:bg-orange-400',
    [TYPE.DANGER]:        'text-white bg-red-600 hover:bg-red-500',
    [TYPE.INFO]:          'text-white bg-gray-500 hover:bg-gray-500',
}[props.type]));

const cellShadowClass = computed(() => ({
    [TYPE.DEFAULT]:       'bg-science-blue-800/10',
    [TYPE.PRIMARY]:       'bg-primary-600/10',
    [TYPE.SECONDARY]:     'bg-secondary-950/10',
    [TYPE.PRIMARY_OLD]:   'bg-science-blue-700/10',
    [TYPE.SECONDARY_OLD]: 'bg-science-blue-800/10',
    [TYPE.SUCCESS]:       'bg-emerald-700/10',
    [TYPE.WARNING]:       'bg-orange-600/10',
    [TYPE.DANGER]:        'bg-red-700/10',
    [TYPE.INFO]:          'bg-gray-500/10',
}[props.type]));

const cellHoverClass = computed(() => ({
    [TYPE.DEFAULT]:       'hover:bg-science-blue-800/10 group-hover:bg-science-blue-800/10',
    [TYPE.PRIMARY]:       'hover:bg-primary-600/10 group-hover:bg-primary-600/10',
    [TYPE.SECONDARY]:     'hover:bg-secondary-950/10 group-hover:bg-secondary-950/10',
    [TYPE.PRIMARY_OLD]:   'hover:bg-science-blue-700/10 group-hover:bg-science-blue-700/10',
    [TYPE.SECONDARY_OLD]: 'hover:bg-science-blue-800/10 group-hover:bg-science-blue-800/10',
    [TYPE.SUCCESS]:       'hover:bg-emerald-700/10 group-hover:bg-emerald-700/10',
    [TYPE.WARNING]:       'hover:bg-orange-600/10 group-hover:bg-orange-600/10',
    [TYPE.DANGER]:        'hover:bg-red-700/10 group-hover:bg-red-700/10',
    [TYPE.INFO]:          'hover:bg-gray-500/10 group-hover:bg-gray-500/10',
}[props.type]));
</script>

<template>
    <div class="flex flex-col gap-y-2">
        <base-calendar
            class="text-sm"
            :value="firstDate"
            :formatter-options="{
                month: 'short',
            }"
        >
            <template #title="{ view: calendarView, date }">
                <div
                    v-if="calendarView === VIEW.MONTH"
                    class="mt-2 flex items-center justify-center px-1 py-2 font-medium text-gray-400"
                >
                    {{ formatDate(date.toISOString(), { weekday: 'short' }) }}
                </div>
            </template>

            <template #cell="{ view: calendarView, date, isToday, isCurrentMonth }">
                <div
                    v-if="calendarView === VIEW.MONTH"
                    class="flex aspect-square w-full cursor-pointer items-center justify-center my-0.5 p-0.75 group"
                    :class="[
                        {
                            'rounded-l-full': areDatesEqual(date, range.start, calendarView) && range.end,
                            'rounded-r-full': areDatesEqual(date, range.end, calendarView) && range.start,
                            [cellShadowClass]: isDateBetween(date, range, calendarView, mode),
                        }
                    ]"
                    @mouseenter="hoveredDate = date"
                    @click="clickCell(date)"
                >
                    <div
                        class="relative flex h-full w-full select-none items-center justify-center rounded-full font-medium"
                        :class="[
                            {
                                'text-gray-300': !isCurrentMonth,
                                'font-semibold': isToday,
                            },
                            isActiveCell(date) ? cellActiveClass : cellHoverClass,
                        ]"
                    >
                        {{ date.getDate() }}

                        <div v-if="isToday" class="absolute mt-4 w-1/2 border-t-2 border-current" />
                    </div>
                </div>
            </template>
        </base-calendar>

        <template v-if="withTime">
            <base-time-picker
                v-if="mode === MODE.SINGLE"
                v-model="model"
                class="self-stretch"
                :hours-format="hoursFormat"
                :with-seconds="withSeconds"
            />

            <template v-if="mode === MODE.RANGE">
                <div class="flex flex-col self-stretch gap-y-0.5">
                    <div>Start Time:</div>
                    <base-time-picker
                        v-model="model.start"
                        :hours-format="hoursFormat"
                        :with-seconds="withSeconds"
                        :disabled="!model.start"
                    />
                </div>

                <div class="flex flex-col self-stretch gap-y-0.5">
                    <div>End Time:</div>
                    <base-time-picker
                        v-model="model.end"
                        :hours-format="hoursFormat"
                        :with-seconds="withSeconds"
                        :disabled="!model.end"
                    />
                </div>
            </template>
        </template>

        <div class="flex items-center gap-x-2 self-stretch">
            <base-button
                v-if="clearable"
                class="mr-auto"
                label="Clear"
                size="small"
                :type="type"
                :disabled="!model"
                text
                @click="clear()"
            />

            <slot name="actions" />
        </div>
    </div>
</template>
